/**
 * Jixxeo AI Joke Generator — the full PWA embed.
 * Home / Generate / Categories / Search / Trending / Favorites / History.
 * AI joke generation via Claude Sonnet server-side.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Sparkles, Shuffle, Heart, HeartOff, Copy, Share2, ThumbsUp, ThumbsDown,
  RefreshCw, Search, Flame, Clock, Star, X, Coins, Bot, Zap, ChevronRight, Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  jokeCategories, jokeGenerate, jokeDaily, jokeTrending, jokeHistory,
  jokeFavorites, jokeFavorite, jokeReact, jokeShare, jokeSearch,
} from "@/lib/api";

const TAB_HOME = "home";
const TAB_TRENDING = "trending";
const TAB_FAVORITES = "favorites";
const TAB_HISTORY = "history";
const TAB_SEARCH = "search";

const CATEGORY_EMOJI = {
  "Dad Jokes": "👨", "Knock Knock": "🚪", "Animal": "🐾", "School": "🎓",
  "Office": "💼", "Technology": "💻", "Gaming": "🎮", "Food": "🍕",
  "Kids": "🧒", "Science": "🔬", "Space": "🚀", "Relationships": "💞",
  "Sports": "🏀", "Movies": "🎬", "Superheroes": "🦸", "Fantasy": "🧙",
  "Pirate": "🏴‍☠️", "Zombie": "🧟", "Halloween": "🎃", "Christmas": "🎄",
  "Valentine's Day": "💘", "AI": "🤖", "Robots": "🦾", "Programming": "⌨️",
  "Puns": "🤓", "One-Liners": "💬", "Silly": "🤡", "Random": "🎲",
  "Dark Humor": "🌑",
};

export default function JixxeoJokeGenerator() {
  const { user, refresh: refreshUser } = useAuth();
  const [tab, setTab] = useState(TAB_HOME);
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState("Random");
  const [prompt, setPrompt] = useState("");
  const [premium, setPremium] = useState(false);
  const [current, setCurrent] = useState(null);   // active joke
  const [busy, setBusy] = useState(false);
  const [daily, setDaily] = useState(null);
  const [trending, setTrending] = useState([]);
  const [history, setHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [favIds, setFavIds] = useState(new Set());
  const [reactions, setReactions] = useState({});   // {jokeId: 'like'|'dislike'|null}
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    jokeCategories().then((r) => setCats(r.categories || [])).catch(() => {});
    jokeDaily().then((r) => setDaily(r.joke)).catch(() => {});
    loadTrending();
    if (user) { loadHistory(); loadFavorites(); }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loadTrending = () => jokeTrending(12).then((r) => setTrending(r.jokes || [])).catch(() => {});
  const loadHistory = () => jokeHistory().then((r) => setHistory(r.jokes || [])).catch(() => {});
  const loadFavorites = () => jokeFavorites().then((r) => {
    setFavorites(r.jokes || []);
    setFavIds(new Set((r.jokes || []).map((j) => j.id)));
  }).catch(() => {});

  const generate = useCallback(async (overrideCat) => {
    if (!user) { toast.error("Log in to generate jokes"); return; }
    setBusy(true);
    try {
      const cat = overrideCat || category;
      const res = await jokeGenerate({ category: cat, prompt, premium });
      setCurrent(res.joke);
      if (premium) toast.success(`-10 coins · balance ${res.coins}`);
      try { await refreshUser?.(); } catch {}
      loadHistory();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't generate joke";
      toast.error(String(msg));
    } finally { setBusy(false); }
  }, [category, prompt, premium, user, refreshUser]);

  const surprise = async () => {
    const random = cats[Math.floor(Math.random() * cats.length)] || "Random";
    setCategory(random);
    await generate(random);
  };

  const toggleFav = async (joke) => {
    if (!user) { toast.error("Log in to favorite"); return; }
    try {
      const res = await jokeFavorite(joke.id);
      setFavIds((s) => {
        const n = new Set(s);
        if (res.favorited) n.add(joke.id); else n.delete(joke.id);
        return n;
      });
      if (res.favorited) toast.success("Favorited!");
      loadFavorites();
    } catch {
      toast.error("Couldn't update favorite");
    }
  };

  const react = async (joke, r) => {
    if (!user) { toast.error("Log in to react"); return; }
    try {
      const res = await jokeReact(joke.id, r);
      const prevReaction = reactions[joke.id];
      const newReaction = res.reaction; // null | "like" | "dislike"
      // Compute visible-count delta so the UI updates immediately for the current joke
      const dLikes =
        (newReaction === "like" ? 1 : 0) - (prevReaction === "like" ? 1 : 0);
      const dDislikes =
        (newReaction === "dislike" ? 1 : 0) - (prevReaction === "dislike" ? 1 : 0);
      const applyDelta = (j) =>
        j && j.id === joke.id
          ? { ...j, likes: Math.max(0, (j.likes || 0) + dLikes), dislikes: Math.max(0, (j.dislikes || 0) + dDislikes) }
          : j;
      setReactions((m) => ({ ...m, [joke.id]: newReaction }));
      setCurrent((c) => applyDelta(c));
      setDaily((d) => applyDelta(d));
      setTrending((list) => list.map(applyDelta));
      setHistory((list) => list.map(applyDelta));
      setFavorites((list) => list.map(applyDelta));
      setSearchResults((list) => list.map(applyDelta));
    } catch { toast.error("Couldn't record reaction"); }
  };

  const copy = async (joke) => {
    try {
      await navigator.clipboard.writeText(`${joke.setup}\n${joke.punchline}\n\n— via Jixxeo AI Jokes`);
      toast.success("Copied!");
    } catch { toast.error("Clipboard blocked"); }
  };

  const shareJoke = async (joke) => {
    const text = `${joke.setup}\n${joke.punchline}\n\n— via Jixxeo AI Jokes`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "A joke for you", text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Copied to share");
      }
      await jokeShare(joke.id).catch(() => {});
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Share cancelled");
    }
  };

  const runSearch = async () => {
    if (!searchQ.trim() && !category) return;
    const res = await jokeSearch(searchQ.trim(), category === "Random" ? "" : category);
    setSearchResults(res.jokes || []);
  };

  const tabs = [
    { id: TAB_HOME, label: "Home", icon: <Sparkles className="w-4 h-4" /> },
    { id: TAB_TRENDING, label: "Trending", icon: <Flame className="w-4 h-4" /> },
    { id: TAB_FAVORITES, label: "Favorites", icon: <Heart className="w-4 h-4" /> },
    { id: TAB_HISTORY, label: "History", icon: <Clock className="w-4 h-4" /> },
    { id: TAB_SEARCH, label: "Search", icon: <Search className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#150425] via-[#050510] to-[#001835] text-white pb-16">
      {/* Hero */}
      <div className="max-w-3xl mx-auto px-4 md:px-8 pt-8">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFE300] to-[#FF9F1C] flex items-center justify-center shadow-[0_0_30px_rgba(255,227,0,0.4)] text-3xl">
            🎭
          </div>
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#FFE300]">// jixxeo apps · ai comedy</div>
            <h1 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tighter leading-none">
              AI Joke Generator
            </h1>
          </div>
          <div className="ml-auto flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">
            <Coins className="w-3.5 h-3.5 text-[#FFE300]" />
            <span className="text-white/80 font-bold" data-testid="joke-coins">{user?.coins ?? "—"}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-6 overflow-x-auto scrollbar-none">
          {tabs.map((t) => (
            <button
              key={t.id} data-testid={`joke-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`shrink-0 mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-full flex items-center gap-1.5 transition-all ${
                tab === t.id ? "bg-[#FFE300] text-black font-black" : "border border-white/10 text-white/60 hover:text-white hover:bg-white/5"
              }`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Home / Generate */}
      {tab === TAB_HOME && (
        <div className="max-w-3xl mx-auto px-4 md:px-8 mt-6 space-y-6">
          {/* Category chips */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="mono text-[10px] uppercase tracking-widest text-white/60">Category</div>
              <span className="mono text-[10px] text-[#FFE300] font-bold">{category}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {cats.map((c) => (
                <button
                  key={c} data-testid={`joke-cat-${c.toLowerCase().replace(/\s+/g,'-').replace("'","")}`}
                  onClick={() => setCategory(c)}
                  className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full transition-all ${
                    category === c ? "bg-white text-black font-black" : "border border-white/10 text-white/70 hover:text-white hover:bg-white/5"
                  }`}>
                  <span className="mr-1">{CATEGORY_EMOJI[c] || "🎯"}</span>{c}
                </button>
              ))}
            </div>
          </div>

          {/* Prompt hint */}
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-white/60 mb-1 block">
              Extra hint (optional)
            </label>
            <input
              data-testid="joke-prompt"
              value={prompt} onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Tell me a joke about cats"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm placeholder:text-white/30 focus:border-[#FFE300]/60 focus:outline-none"
            />
          </div>

          {/* Premium toggle + big generate */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-white/60">Premium (extra clever)</div>
              <div className="mono text-[10px] text-[#FFE300]">Costs 10 coins per generation</div>
            </div>
            <button
              data-testid="joke-premium-toggle"
              onClick={() => setPremium((p) => !p)}
              className={`px-3 py-1.5 rounded-full mono text-[10px] uppercase tracking-widest font-bold transition-colors ${
                premium ? "bg-[#FFE300] text-black" : "border border-white/15 text-white/60"
              }`}>
              {premium ? "ON" : "OFF"}
            </button>
          </div>

          <div className="flex gap-2">
            <button
              data-testid="joke-generate"
              onClick={() => generate()} disabled={busy}
              className="flex-1 py-4 rounded-2xl bg-gradient-to-br from-[#FFE300] to-[#FF9F1C] hover:brightness-110 text-black font-black text-lg uppercase tracking-tighter transition-all disabled:opacity-40 shadow-[0_10px_30px_rgba(255,159,28,0.35)] flex items-center justify-center gap-2">
              {busy ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
              {busy ? "Cooking joke…" : "Generate Joke"}
            </button>
            <button
              data-testid="joke-surprise"
              onClick={surprise} disabled={busy}
              title="Surprise me — random category"
              className="px-4 rounded-2xl border-2 border-white/15 hover:border-[#FFE300]/40 hover:bg-white/5 flex items-center justify-center disabled:opacity-40">
              <Shuffle className="w-5 h-5" />
            </button>
          </div>

          {/* Active joke */}
          {current && (
            <JokeCard
              joke={current}
              favorited={favIds.has(current.id)}
              reaction={reactions[current.id]}
              onFav={() => toggleFav(current)}
              onReact={(r) => react(current, r)}
              onCopy={() => copy(current)}
              onShare={() => shareJoke(current)}
              onSimilar={() => generate()}
              testId="joke-active"
            />
          )}

          {/* Daily joke */}
          {daily && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="mono text-[10px] uppercase tracking-widest text-[#39FF14]">// joke of the day</div>
                <Trophy className="w-4 h-4 text-[#39FF14]" />
              </div>
              <JokeCard
                joke={daily} compact
                favorited={favIds.has(daily.id)}
                reaction={reactions[daily.id]}
                onFav={() => toggleFav(daily)}
                onReact={(r) => react(daily, r)}
                onCopy={() => copy(daily)}
                onShare={() => shareJoke(daily)}
                testId="joke-daily"
              />
            </div>
          )}

          {/* Recent 3 from history */}
          {history.length > 0 && (
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-white/60 mb-2">// recent</div>
              <div className="space-y-2">
                {history.slice(0, 3).map((j) => (
                  <MiniJoke key={j.id} joke={j} onClick={() => setCurrent(j)} />
                ))}
              </div>
              <button onClick={() => setTab(TAB_HISTORY)} className="mt-2 mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white flex items-center gap-1">
                See all history <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Trending */}
      {tab === TAB_TRENDING && (
        <div className="max-w-3xl mx-auto px-4 md:px-8 mt-6 space-y-3" data-testid="joke-trending-list">
          {trending.length === 0 && <EmptyState icon={<Flame />} label="No trending jokes yet. Be the first to make people laugh." />}
          {trending.map((j) => (
            <JokeCard
              key={j.id} joke={j}
              favorited={favIds.has(j.id)}
              reaction={reactions[j.id]}
              onFav={() => toggleFav(j)}
              onReact={(r) => react(j, r)}
              onCopy={() => copy(j)}
              onShare={() => shareJoke(j)}
            />
          ))}
        </div>
      )}

      {/* Favorites */}
      {tab === TAB_FAVORITES && (
        <div className="max-w-3xl mx-auto px-4 md:px-8 mt-6 space-y-3" data-testid="joke-favorites-list">
          {favorites.length === 0 && <EmptyState icon={<Heart />} label="No favorites yet. Tap ♥ on any joke to save it." />}
          {favorites.map((j) => (
            <JokeCard
              key={j.id} joke={j}
              favorited
              reaction={reactions[j.id]}
              onFav={() => toggleFav(j)}
              onReact={(r) => react(j, r)}
              onCopy={() => copy(j)}
              onShare={() => shareJoke(j)}
            />
          ))}
        </div>
      )}

      {/* History */}
      {tab === TAB_HISTORY && (
        <div className="max-w-3xl mx-auto px-4 md:px-8 mt-6 space-y-3" data-testid="joke-history-list">
          {history.length === 0 && <EmptyState icon={<Clock />} label="No history yet. Generate your first joke!" />}
          {history.map((j) => (
            <JokeCard
              key={j.id} joke={j} compact
              favorited={favIds.has(j.id)}
              reaction={reactions[j.id]}
              onFav={() => toggleFav(j)}
              onReact={(r) => react(j, r)}
              onCopy={() => copy(j)}
              onShare={() => shareJoke(j)}
            />
          ))}
        </div>
      )}

      {/* Search */}
      {tab === TAB_SEARCH && (
        <div className="max-w-3xl mx-auto px-4 md:px-8 mt-6 space-y-3">
          <div className="flex gap-2">
            <input
              data-testid="joke-search-input"
              value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Search all jokes (setup, punchline, tags)"
              className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm placeholder:text-white/30 focus:border-[#FFE300]/60 focus:outline-none"
            />
            <button data-testid="joke-search-btn" onClick={runSearch}
              className="px-5 rounded-xl bg-[#FFE300] hover:brightness-110 text-black font-black mono text-xs uppercase tracking-widest">
              Search
            </button>
          </div>
          <div className="space-y-2" data-testid="joke-search-results">
            {searchResults.length === 0 && <EmptyState icon={<Search />} label="Enter a keyword above." />}
            {searchResults.map((j) => (
              <JokeCard
                key={j.id} joke={j}
                favorited={favIds.has(j.id)}
                reaction={reactions[j.id]}
                onFav={() => toggleFav(j)}
                onReact={(r) => react(j, r)}
                onCopy={() => copy(j)}
                onShare={() => shareJoke(j)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Sub-components
   ============================================================ */

function JokeCard({ joke, favorited, reaction, onFav, onReact, onCopy, onShare, onSimilar, compact = false, testId }) {
  return (
    <div data-testid={testId || `joke-card-${joke.id}`}
      className="rounded-2xl p-5 bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 hover:border-[#FFE300]/40 transition-colors">
      <div className="flex items-center gap-1.5 mono text-[9px] uppercase tracking-widest mb-2">
        <span className="text-[#FFE300]">// {joke.category}</span>
        {joke.ai && <span className="text-white/40">· AI</span>}
        {joke.premium && <span className="text-[#39FF14]">· PREMIUM</span>}
      </div>
      <div className={`font-heading font-black tracking-tight text-white ${compact ? "text-lg" : "text-xl md:text-2xl"} mb-2`}>
        {joke.setup}
      </div>
      <div className={`font-heading text-[#FFE300] ${compact ? "text-base" : "text-lg md:text-xl"}`}>
        {joke.punchline}
      </div>
      {(joke.tags?.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {joke.tags.map((t) => (
            <span key={t} className="mono text-[9px] uppercase tracking-widest text-white/40">#{t}</span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 mt-4">
        <IconBtn testId="joke-fav" active={favorited} activeColor="#FF003C" onClick={onFav}>
          {favorited ? <Heart className="w-4 h-4 fill-current" /> : <Heart className="w-4 h-4" />}
        </IconBtn>
        <IconBtn testId="joke-like" active={reaction === "like"} activeColor="#39FF14" onClick={() => onReact("like")}>
          <ThumbsUp className="w-4 h-4" /> <span className="mono text-[10px] font-bold ml-1">{joke.likes ?? 0}</span>
        </IconBtn>
        <IconBtn testId="joke-dislike" active={reaction === "dislike"} activeColor="#FF003C" onClick={() => onReact("dislike")}>
          <ThumbsDown className="w-4 h-4" />
        </IconBtn>
        <IconBtn testId="joke-copy" onClick={onCopy}><Copy className="w-4 h-4" /></IconBtn>
        <IconBtn testId="joke-share" onClick={onShare}><Share2 className="w-4 h-4" /></IconBtn>
        {onSimilar && (
          <IconBtn testId="joke-similar" onClick={onSimilar}>
            <RefreshCw className="w-4 h-4" />
          </IconBtn>
        )}
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, active, activeColor = "#FFE300", testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="h-8 min-w-[32px] px-2 rounded-lg border flex items-center justify-center transition-colors"
      style={{
        borderColor: active ? `${activeColor}80` : "rgba(255,255,255,0.1)",
        background: active ? `${activeColor}20` : "transparent",
        color: active ? activeColor : "rgba(255,255,255,0.7)",
      }}>
      {children}
    </button>
  );
}

function MiniJoke({ joke, onClick }) {
  return (
    <button onClick={onClick}
      className="w-full text-left rounded-xl p-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 hover:border-white/10 transition-colors">
      <div className="mono text-[9px] uppercase tracking-widest text-white/40">// {joke.category}</div>
      <div className="font-heading font-bold text-white text-sm truncate">{joke.setup}</div>
    </button>
  );
}

function EmptyState({ icon, label }) {
  return (
    <div className="rounded-2xl p-8 border border-dashed border-white/10 text-center text-white/50">
      <div className="w-10 h-10 rounded-full bg-white/5 mx-auto flex items-center justify-center mb-2 text-white/60">{icon}</div>
      <div className="mono text-[10px] uppercase tracking-widest">{label}</div>
    </div>
  );
}
